import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import { Button, Icon, Popover } from '@patternfly/react-core';
import { QuestionCircleIcon } from '@patternfly/react-icons';

import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';

import './styles.scss';
import { labEnvironmentContext } from '../store';

type LabComponent = {
  context?: {
    project_id?: string;
    project_name?: string;
    public_ip?: string;
  };
  driver: string;
  region?: string;
  slug?: string;
};

const NovelloLabInfo = () => {
  const { t } = useTranslation();
  const store = useContext(labEnvironmentContext);

  const { userLab } = store;

  const {
    billing_region: region,
    components,
    lab_definition_id: labDefinitionId,
    shared_offering_slugs_list: sharedOfferingSlugs,
    state,
  } = userLab;

  const fc: LabComponent | undefined =
    components && components.length
      ? components.find((c: LabComponent) => c.driver === 'openstack')
      : undefined;

  // eslint-disable-next-line camelcase
  const projectId = fc?.context?.project_id ?? null;
  // eslint-disable-next-line camelcase
  const projectName = fc?.context?.project_name ?? null;
  // eslint-disable-next-line camelcase
  const publicIp = fc?.context?.public_ip ?? null;

  const labInfoBody = (
    <>
      <p>
        <strong>{t('Project id')}:</strong> {projectId || t('Not available')}
      </p>

      <p>
        <strong>{t('Project name')}:</strong>{' '}
        {projectName || t('Not available')}
      </p>

      <p>
        <strong>{t('Project state')}:</strong> {state}
      </p>

      <p>
        <strong>{t('Lab definition id')}:</strong> {labDefinitionId}
      </p>

      {sharedOfferingSlugs && (
        <p>
          <strong>{t('Shared offering slugs')}:</strong>{' '}
          {sharedOfferingSlugs.join(', ')}
        </p>
      )}

      <p>
        <strong>{t('Published region')}:</strong> {region}
      </p>

      {!!(components && components.length) &&
        components.map((component: LabComponent) => (
          <p key={component.slug}>
            <strong>
              {component.slug} {t('region')}:
            </strong>{' '}
            {component.region}
          </p>
        ))}
    </>
  );

  return (
    <div className="lab-controls-info">
      {publicIp && (
        <p className="lab-controls-info-public-ip">
          {t('Public IP')}: {publicIp}
        </p>
      )}
      <Popover
        aria-label={t('Lab information')}
        bodyContent={labInfoBody}
        className="lab-controls-info-panel"
        headerContent={t('Lab information')}
      >
        <Button
          className="lab-controls-info-btn"
          data-analytics-id="info-icon-vt-dash-lp"
          variant="plain"
        >
          <Icon>
            <QuestionCircleIcon />
          </Icon>
        </Button>
      </Popover>
    </div>
  );
};

export default withErrorBoundary(
  observer(NovelloLabInfo),
  ComponentError,
  handleError,
);
